import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  Radio,
  Space,
  TableProps,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AddIcon from "../../assets/icons/AddIcon";
import DateIcon from "../../assets/icons/DateIcon";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import SortIcon from "../../assets/icons/SortIcon";
import SuccessModal from "../../components/Report/SuccessModal";
import CustomModal from "../../components/ui/CustomModal";
import CustomTable from "../../components/ui/CustomTable";
import colors from "../../constants/colors";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { toast } from "../../lib/toast";
import AdditionsAddEmployeeModal from "../../pages/admin/Settings/Payroll/AdditionsAddEmployeeModal";
import { EmployeeData, ModalComponentProps } from "../../types";
import { capitalize } from "../../utils/string";
import { addKeyInArray } from "../../utils/utils";
import { useGetDepartmentsQuery } from "../department/departmentApi";
import { useReGenerateSalaryNewMutation } from "./payrollApi";

export default function ReGenerateSalaryModal({
  handleClose,
  ...rest
}: ModalComponentProps) {
  // const navigate=useNavigate();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number[]>([]);
  const [form] = Form.useForm();
  const [
    reGenerateSalary,
    {
      isLoading: isReGenerateLoading,
      isError: isReGenerateError,
      error: reGenerateError,
      isSuccess: isReGenerateSuccess,
      // data: reGenerateData,
    },
  ] = useReGenerateSalaryNewMutation();
  useError(isReGenerateError, reGenerateError, "Re-generate data fail");
  const {
    handleOpen,
    open: childOpen,
    handleClose: childClose,
  } = useModal<undefined, "SUCCESS">();

  const onFinish = (values: any) => {
    const { selectedItem, month_year, department_ids } = values;
    if (selectedItem === "employee" && !selectedEmployeeId?.length) {
      toast("error", "Please select employee");
      return;
    }

    const newValues = {
      month_year: month_year?.format("MM-YYYY"),
      business_member_ids:
        selectedItem === "employee" ? selectedEmployeeId : undefined,
      department_ids:
        selectedItem === "department" ? department_ids : undefined,
      type: selectedItem === "global" ? "global" : undefined,
      task_name: "Re-generate Salary",
    };

    reGenerateSalary(newValues);
  };

  useEffect(() => {
    if (isReGenerateSuccess) {
      toast("success", "Salary successfully re-generated");
      // if (reGenerateData?.status === "success") {
      //   navigate("/admin/task-queue");
      // }
      // handleClose(true);
      handleOpen({ type: "SUCCESS" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReGenerateSuccess]);

  return (
    <CustomModal
      title="Re-generate Salary"
      handleClose={handleClose}
      {...rest}
      footer={null}
      width={700}
      // maskClosable={false}
    >
      {childOpen?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={childOpen?.open}
          handleClose={childClose}
        />
      )}
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        form={form}
        onFinish={onFinish}
      >
        <Form.Item
          name="month_year"
          label="Select Month"
          className="mb-4"
          rules={[
            {
              required: true,
              message: "Please select month",
            },
          ]}
        >
          <DatePicker
            format="MMMM YYYY"
            suffixIcon={<DateIcon width={18} height={20} />}
            style={{ width: "100%" }}
            size="large"
            picker="month"
            disabledDate={(current) => current && current.isAfter(dayjs())}
          />
        </Form.Item>
        <Form.Item
          name={"selectedItem"}
          className="mb-0"
          initialValue={"global"}
          label="Type"
        >
          <Radio.Group className="custom-radio text-header text-sm font-medium">
            {typeListEffective?.map((val) => (
              <Radio value={val?.value} key={val?.value}>
                {capitalize(val?.label)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <div className="mb-2" />

        <SelectedItemData
          form={form}
          setSelectedEmployeeId={setSelectedEmployeeId}
        />

        <Space className="w-full p-4 sticky bottom-0 bg-white" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            size="middle"
            htmlType="submit"
            loading={isReGenerateLoading}
          >
            Submit
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}

export type ListComponentType = {
  form: FormInstance<any>;
  setSelectedEmployeeId: (value: number[]) => void;
};

const SelectedItemData = ({
  form,
  setSelectedEmployeeId,
}: ListComponentType) => {
  const [selectedData, setSelectedData] = useState<number[]>([]);
  const { open, handleClose, handleOpen } = useModal<EmployeeData[]>();
  const effectiveFor = Form.useWatch("selectedItem", form);
  const departmentIds = Form.useWatch("department_ids", form);
  const [employeeList, setEmployeeList] = useState<EmployeeData[]>([]);
  const [checkAll, setCheckAll] = useState(false);

  const handleRemoveEmployee = () => {
    const updatedList = employeeList?.filter(
      (item) => !selectedData?.some((el) => el === item?.id)
    );
    setEmployeeList(updatedList);
    setSelectedData([]);
  };

  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery(
    {
      params: { limit: 0, skip: 0, is_published: 1 },
    },
    {
      skip: effectiveFor !== "department",
    }
  );
  useError(isDepartmentError, departmentError, "Get department fail");

  useEffect(() => {
    setSelectedEmployeeId(employeeList?.map((item) => item?.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeList]);

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: () => (
        <span className="whitespace-no-wrap block">Employee Id</span>
      ),
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) => val ?? "n/a",
    },
    {
      title: () => (
        <span className="whitespace-no-wrap block">Employee Name</span>
      ),
      dataIndex: "member",
      render: (val) => val?.profile?.name ?? "n/a",
      width: 200,
    },
    {
      title: () => <span className="whitespace-no-wrap block">Email</span>,
      dataIndex: "member",
      render: (val) => val?.profile?.email ?? "n/a",
    },
  ];

  const handleCheckAll = (checked: boolean) => {
    const allDepartmentIds =
      departmentData?.data?.departments?.map((item) => item?.id) || [];
    form.setFieldValue("department_ids", checked ? allDepartmentIds : []);
    setCheckAll(checked);
  };

  if (effectiveFor === "department") {
    return (
      <Form.Item
        name="department_ids"
        label={
          <div className="flex items-center justify-between gap-4">
            <span>Department:</span>
            <Checkbox
              checked={
                departmentIds?.length ===
                departmentData?.data?.departments?.length
              }
              onChange={(e) => handleCheckAll(e.target.checked)}
              className="text-sm font-medium flex items-center select-none"
            >
              Select All
            </Checkbox>
          </div>
        }
        className="mb-2"
        rules={[
          {
            required: true,
            message: "Please select department",
          },
        ]}
      >
        <Checkbox.Group className="w-full">
          <div className="grid grid-cols-4 gap-x-1 gap-y-2 p-2.5 w-full border-2 border-light border-dashed">
            {departmentData?.data?.departments?.map((item) => (
              <Checkbox
                key={item?.id}
                value={item?.id}
                className="text-header !text-sm font-medium"
              >
                {item?.name}
              </Checkbox>
            ))}
          </div>
        </Checkbox.Group>
      </Form.Item>
    );
  }

  if (effectiveFor === "employee") {
    return (
      <div>
        {open.type === "VIEW" && (
          <AdditionsAddEmployeeModal
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
            onChange={(employees) => setEmployeeList(employees)}
          />
        )}

        <div className="flex justify-between items-center !bg-[#F0F3F9] pt-1 pb-1 pl-2 pr-2 rounded-md mb-2">
          <div className="text-[16px] font-semibold text-[#2D3643]">
            Selected Employee
          </div>
          <div>
            {selectedData?.length ? (
              <Button
                className="outline-button-primary border-none bg-transparent"
                size="small"
                icon={<DeleteIcon />}
                onClick={handleRemoveEmployee}
              >
                Remove Employee
              </Button>
            ) : (
              <Button
                className="outline-button-primary border-none bg-transparent"
                size="small"
                icon={<AddIcon color={colors.darkHighlight} />}
                onClick={() => {
                  handleOpen({ type: "VIEW", data: employeeList });
                }}
              >
                Add Employee
              </Button>
            )}
          </div>
        </div>

        <CustomTable
          style={{ border: "none" }}
          columns={columns}
          dataSource={addKeyInArray(employeeList)}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as number[]);
            },
            columnWidth: 40,
          }}
        />
      </div>
    );
  }

  return null;
};

const typeListEffective = [
  { label: "Global", value: "global" },
  { label: "Department", value: "department" },
  { label: "Employee", value: "employee" },
];
